import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloudComputingsService from '../../services/claim/cloud_computings.service';
import { CloudComputingsHeader, CloudComputingsRow, CloudComputingsTotalsRow } from '.';
import { Alert, Card, Row, Col, Table } from 'react-bootstrap';
import { Loading } from '@rd-web-markets/shared/dist/util';
import ClaimStateAlerts from '@rd-web-markets/shared/dist/claim/ClaimStateAlerts';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { createTimezonedDate } from '@rd-web-markets/shared/dist/util/date/dateUtils';
import { AddButton } from '@rd-web-markets/shared/dist/util/buttons';
import SectionsFooter from '../SectionsFooter';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
export const CloudComputingsSection = _ref => {
  let {
    accountType,
    claim,
    refreshClaim,
    setClaim,
    totalPayrollCostInvalid,
    showCostFragmentsTable,
    allProjects
  } = _ref;
  const [cloudComputings, setCloudComputings] = useState(null);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const claimId = useMemo(() => claim === null || claim === void 0 ? void 0 : claim.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  const getData = useErrorHandling(useCallback(async () => {
    setCloudComputings(await cloudComputingsService.all(claimId));
  }, [claimId]));
  useEffect(() => {
    getData();
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const cloudComputing = cloudComputings[index];
    if (cloudComputing.id) {
      try {
        await cloudComputingsService.delete(cloudComputing);
        const cloudComputings = await cloudComputingsService.all(claim.id);
        setCloudComputings(cloudComputings);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newCloudComputings = [...cloudComputings];
      newCloudComputings.splice(index, 1);
      setCloudComputings(newCloudComputings);
    }
  };
  const handleAdd = cloudComputing => {
    setCloudComputings([...cloudComputings, cloudComputing]);
  };
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  const userUnauthorizedToEdit = !hasPermissions();
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !cloudComputings) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimStateAlerts, {
    claim: claim
  }), /*#__PURE__*/React.createElement(Alert, {
    variant: "info"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "info"), " Please ensure that:", /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, "all costs were incurred between ", createTimezonedDate(claim.from_date).toCustomLocaleDateString(), " and ", createTimezonedDate(claim.to_date).toCustomLocaleDateString()), /*#__PURE__*/React.createElement("li", null, "total Utilities cost amount matches the financial accounts P&L"))), /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "cloud_computings",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  })), totalPayrollCostInvalid ? /*#__PURE__*/React.createElement(Alert, {
    variant: "warning"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "warning"), " Total payroll cost is either empty or smaller than the sum of employee costs. Please go to the Staffing Costs tab to correct this.") : null, !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, cloudComputings.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(CloudComputingsHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, cloudComputings.map((cloudComputing, index) => {
    return /*#__PURE__*/React.createElement(CloudComputingsRow, {
      key: index,
      index: index,
      sortedClaimProjectReports: sortedClaimProjectReports,
      headerScrollPosition: headerScrollPosition,
      claim: claim,
      handleDelete: () => handleDelete(index),
      cloudComputings: cloudComputings,
      setCloudComputings: setCloudComputings,
      refreshClaim: refreshClaim,
      totalPayrollCostInvalid: totalPayrollCostInvalid,
      handleError: handleError,
      cloudComputingsService: cloudComputingsService,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(CloudComputingsTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(AddButton, {
    text: "Add Row",
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_employee_rnd_apportionments: []
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  })))), showCostFragmentsTable && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::CloudComputing",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  }), /*#__PURE__*/React.createElement(SectionsFooter, {
    claim: claim,
    accountType: accountType,
    backLink: "/".concat(accountType, "/claims/").concat(claim.id, "/financial_details/software"),
    continueLink: "/".concat(accountType, "/claims/").concat(claim.id, "/financial_details/data_licences_costs")
  }));
};